import { connect } from 'react-redux';
import { ARRAY_ERROR } from 'final-form';
import { FormSpy } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { useCallback, useMemo } from 'react';

import { KnmTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import { IKnmReasonTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmReasonTypeSelectOption';
import { IKnmTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmTypeSelectOption';
import { getReasonBlockId, REASONS_TITLE_BLOCK_ID } from 'KnmPackage/MainModule/constants/fieldsElementsIds';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmErpForm } from 'KnmErpPackage/MainModule/interfaces/IKnmErpForm';
import {
  getKnmData,
  getKnmDataCreated,
  getKnmFormRef,
  getKnmReasonTypesSelectOptions,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';

import { IKnmErpFormReason } from '../interfaces/IKnmErpFormReason';
import styles from './KnmErpReasons.module.scss';
import { KnmReasonTypeCodesEnum } from '../enums/KnmReasonTypeCodesEnum';

const planTypeRestrictedStartMonthIndex = 0;
const planTypeRestrictedEndMonthIndex = 6;

interface IKnmErpReasonsProps {
  typeCode: IKnmTypeSelectOption['code'] | undefined;
  watchOnly?: boolean;
  formKnmReasons: IKnmErpFormReason[] | undefined;
  startDate: string | undefined;
  formRef: IKnmFormRef | undefined;
  knmReasonTypesSelectOptions: IKnmReasonTypeSelectOption[] | undefined;
  created: string | null | undefined;
  yearKnm: number | null | undefined;
}

const KnmErpReasonsWithoutStore = ({
  typeCode,
  watchOnly,
  formKnmReasons,
  formRef,
  knmReasonTypesSelectOptions,
  created,
  yearKnm,
  startDate,
}: IKnmErpReasonsProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const isCurrentDateInPlanTypeRestrictRange =
    currentMonth >= planTypeRestrictedStartMonthIndex && currentMonth <= planTypeRestrictedEndMonthIndex;
  const isPlanType = typeCode === KnmTypeCodesEnum.PP;
  const isReasonsPlanTypeRestricted = isPlanType && isCurrentDateInPlanTypeRestrictRange;

  const handleReasonTypeChange = (index: number) => () => {
    formRef?.current.change(`reasons[${index}].requirementDetails` as keyof IKnmErpForm);
    formRef?.current.change(`reasons[${index}].assignmentNumber` as keyof IKnmErpForm);
    formRef?.current.change(`reasons[${index}].assignmentDate` as keyof IKnmErpForm);
  };

  const filteredKnmReasonTypesOptions = useMemo(
    () =>
      !isReasonsPlanTypeRestricted
        ? knmReasonTypesSelectOptions
        : knmReasonTypesSelectOptions?.filter(knmReasonTypeOption => {
            if ((created && yearKnm) || startDate) {
              const currentYear = new Date().getFullYear();
              const createdYear = new Date(created || '').getFullYear();
              const startDateYear = startDate
                ? parseDate({ date: startDate, parseFormat: DATE_FORMAT_LONG_FNS })?.getFullYear()
                : undefined;

              if (
                createdYear - currentYear === -1 ||
                (yearKnm && yearKnm > createdYear) ||
                (startDateYear && startDateYear > currentYear)
              ) {
                return Object.keys(KnmReasonTypeCodesEnum).includes(knmReasonTypeOption.code);
              }
              if (yearKnm === createdYear) {
                return knmReasonTypeOption.code === KnmReasonTypeCodesEnum.RSN_PP_IV;
              }
            }

            return knmReasonTypeOption.code === KnmReasonTypeCodesEnum.RSN_PP_IV;
          }),
    [created, isReasonsPlanTypeRestricted, knmReasonTypesSelectOptions, startDate, yearKnm],
  );
  const renderKnmReasonTypesOptions = (
    items: IKnmReasonTypeSelectOption[] | undefined,
    reasonFieldValue: IKnmErpFormReason | undefined,
  ) => {
    return items?.map(item => ({
      ...item,
      isDisabled: formKnmReasons?.some(
        formKnmReason =>
          ((reasonFieldValue && item.id !== reasonFieldValue.type?.id) || true) && formKnmReason?.type?.id === item.id,
      ),
    }));
  };

  const addReasonEvent = useCallback(
    () =>
      !watchOnly &&
      push &&
      push('reasons', !isPlanType ? { listId: uuidv4() } : { approveRequired: true, listId: uuidv4() }),
    [isPlanType, push, watchOnly],
  );

  const makeRemoveReasonEventHandler = (index: number) => () => !watchOnly && remove && remove('reasons', index);

  return (
    <FieldBlock
      label="Основания регистрации КНМ"
      id={REASONS_TITLE_BLOCK_ID}
      button={
        <Button
          className="shared-btn-add"
          text="Добавить"
          variant="link"
          color="primary"
          type="button"
          disabled={watchOnly}
          onClick={addReasonEvent}
        />
      }
    >
      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          const reasonsFieldError = props.errors?.reasons?.[ARRAY_ERROR];
          const isReasonsFieldTouched = props.touched?.reasons;

          if (reasonsFieldError && isReasonsFieldTouched) {
            return <p className="shared-input-error">{reasonsFieldError}</p>;
          }

          return null;
        }}
      </FormSpy>
      <Row>
        <FieldArray name="reasons">
          {({ fields }) => {
            if (!fields.length) {
              return null;
            }

            return fields.map((name, index) => {
              const reasonFieldValue: IKnmErpFormReason = fields.value?.[index];
              const id = getReasonBlockId(reasonFieldValue?.listId);

              return (
                <Row key={id} id={id} isListItem margin="medium">
                  <div className="shared-deleting-row">
                    <SelectInputField
                      placeholder="Выберите основание регистрации КНМ"
                      name={`${name}.type`}
                      id={`${name}.type`}
                      options={renderKnmReasonTypesOptions(filteredKnmReasonTypesOptions, reasonFieldValue)}
                      isSearchable
                      disabled={watchOnly}
                      size="medium"
                      onChange={handleReasonTypeChange(index)}
                      className={styles.ReasonSelectorField}
                    />
                    {reasonFieldValue?.type?.hasText && (
                      <TextareaField
                        name={`${name}.text`}
                        placeholder="Введите иное основание регистрации КНМ"
                        disabled={watchOnly}
                      />
                    )}
                    {reasonFieldValue?.type?.dateRequired && (
                      <DatePickerField
                        className={styles.ReasonDateField}
                        name={`${name}.date`}
                        placeholder="Выберите дату"
                        disabled={watchOnly}
                      />
                    )}
                    {(reasonFieldValue.type?.requirementDetailsRequired ||
                      reasonFieldValue.type?.detailsRequirement) && (
                      <FieldBlock label="Реквизиты требования" placementLabel="left" color="black">
                        <TextareaField
                          className="shared-row-field"
                          id={`${name}.requirementDetails`}
                          name={`${name}.requirementDetails`}
                          disabled={watchOnly}
                        />
                      </FieldBlock>
                    )}
                    {reasonFieldValue?.type?.assignmentNumberRequired &&
                      reasonFieldValue?.type?.assignmentDateRequired && (
                        <Row>
                          <FieldBlock size="half" label="Номер поручения" placementLabel="left" color="black">
                            <TextareaField
                              className="shared-row-field"
                              id={`${name}.assignmentNumber`}
                              name={`${name}.assignmentNumber`}
                              disabled={watchOnly}
                            />
                          </FieldBlock>
                          <FieldBlock size="half" label="Дата поручения" placementLabel="left" color="black">
                            <DatePickerField
                              className="shared-row-field"
                              id={`${name}.assignmentDate`}
                              name={`${name}.assignmentDate`}
                              disabled={watchOnly}
                            />
                          </FieldBlock>
                        </Row>
                      )}
                    <CheckboxField
                      className={styles.ReasonApproveField}
                      labelClassName={styles.ReasonApproveFieldLabel}
                      id={`${name}.approveRequired`}
                      name={`${name}.approveRequired`}
                      label="Требуется согласование с органом прокуратуры"
                      size="medium"
                      type="checkbox"
                      theme="system"
                      disabled={watchOnly || isPlanType}
                    />
                  </div>
                  <CloseButton className="shared-deleting-row-btn" onClick={makeRemoveReasonEventHandler(index)} />
                </Row>
              );
            });
          }}
        </FieldArray>
      </Row>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState) => ({
  formRef: getKnmFormRef(state),
  knmReasonTypesSelectOptions: getKnmReasonTypesSelectOptions(state),
  created: getKnmDataCreated(state),
  yearKnm: getKnmData(state)?.year,
});

export const KnmErpReasons = connect(mapStateToProps)(KnmErpReasonsWithoutStore);
